import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BookOpen, Save, Check, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export function AdminPageExplainersTab() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [form, setForm] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/page-explainers`, { credentials: 'include' });
      if (res.ok) setPages(await res.json());
      else toast.error('Failed to load explainers');
    } catch { toast.error('Failed to load explainers'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activeKey) { setForm({ title: '', content: '' }); return; }
    const p = pages.find(x => x.page_key === activeKey);
    setForm({ title: p?.title || '', content: p?.content || '' });
  }, [activeKey, pages]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/page-explainers/${activeKey}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title.trim(), content: form.content.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success(form.content.trim() ? 'Explainer saved' : 'Explainer cleared');
      await load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!form.content.trim()) return;
    if (!(await window.appConfirm('Clear this page explainer? The info icon will disappear from that page.'))) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/page-explainers/${activeKey}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: '', content: '' }),
      });
      if (!res.ok) throw new Error();
      toast.success('Cleared');
      setForm({ title: '', content: '' });
      await load();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-page-explainers-tab">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-brand-navy" /> Page Explainers
        </h3>
        <p className="text-sm text-slate-500">
          Author a short explainer for each main page. A small info icon appears on the page so members can read your guidance and dismiss it.
          Leave content empty to remove the icon from a page.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Page picker */}
        <Card className="p-0 overflow-hidden md:col-span-1" data-testid="explainer-page-list">
          {loading ? (
            <p className="text-sm text-slate-500 p-4">Loading…</p>
          ) : (
            <ul className="divide-y">
              {pages.map(p => (
                <li key={p.page_key}>
                  <button type="button" onClick={() => setActiveKey(p.page_key)}
                    data-testid={`explainer-pick-${p.page_key}`}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors text-left ${
                      activeKey === p.page_key ? 'bg-brand-navy/5 border-l-4 border-brand-navy font-semibold text-brand-navy' : 'text-slate-700'
                    }`}>
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{p.page_label}</span>
                    {p.content ? (
                      <Check className="h-3.5 w-3.5 text-brand-sage flex-shrink-0" />
                    ) : (
                      <span className="text-[10px] text-slate-300 uppercase tracking-wide">empty</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Editor */}
        <Card className="p-4 md:col-span-2" data-testid="explainer-editor">
          {!activeKey ? (
            <div className="text-center py-12 text-sm text-slate-400">
              <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              Pick a page on the left to edit its explainer.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Editing: <span className="font-semibold text-slate-700">{pages.find(p => p.page_key === activeKey)?.page_label}</span>
                {pages.find(p => p.page_key === activeKey)?.updated_at && (
                  <span className="ml-2">· Last updated {new Date(pages.find(p => p.page_key === activeKey).updated_at).toLocaleDateString()} by {pages.find(p => p.page_key === activeKey).updated_by_name}</span>
                )}
              </p>
              <div>
                <Label htmlFor="explainer-title">Title (optional)</Label>
                <Input id="explainer-title" data-testid="explainer-title-input"
                  value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder='e.g. "About the Forum"' />
              </div>
              <div>
                <Label htmlFor="explainer-content">Content</Label>
                <Textarea id="explainer-content" data-testid="explainer-content-input"
                  value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                  placeholder="Write what members should know about this page — what it's for, how to use it, what to look out for…"
                  className="min-h-[260px] font-mono text-sm" />
                <p className="text-[11px] text-slate-400 mt-1">Plain text supported. Line breaks will be preserved.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={handleSave} disabled={saving}
                  data-testid="explainer-save-btn"
                  className="bg-brand-navy text-white hover:bg-brand-dark">
                  <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
                </Button>
                {form.content.trim() && (
                  <Button onClick={handleClear} variant="outline"
                    data-testid="explainer-clear-btn"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear explainer
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
